import Interceptor from "./Interceptor";

/**
 * Admin API Service - Admin-only endpoints
 * Note: Interceptor already includes /api prefix from REACT_APP_API_BASE
 */

// Dashboard
export const getAdminDashboard = async () => {
  const response = await Interceptor.get("/admin/dashboard");
  return response.data;
};

// System Settings
export const getSystemSettings = async () => {
  const response = await Interceptor.get("/admin/settings");
  return response.data;
};

export const updateSystemSettings = async (settings) => {
  const response = await Interceptor.put("/admin/settings", { settings });
  return response.data;
};

// Activities
export const getAdminActivities = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") queryParams.set(key, value);
  });
  const response = await Interceptor.get(`/admin/activities?${queryParams}`);
  return response.data;
};

// Sites
export const getAdminSites = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") queryParams.set(key, value);
  });
  const response = await Interceptor.get(`/admin/sites?${queryParams}`);
  return response.data;
};

// Plans Management
export const getAdminPlans = async () => {
  const response = await Interceptor.get("/admin/plans");
  return response.data;
};

export const createPlan = async (planData) => {
  const response = await Interceptor.post("/admin/plans", planData);
  return response.data;
};

export const updatePlan = async (planId, planData) => {
  const response = await Interceptor.put(`/admin/plans/${planId}`, planData);
  return response.data;
};

export const deletePlan = async (planId) => {
  const response = await Interceptor.delete(`/admin/plans/${planId}`);
  return response.data;
};

// ==================== Site Management ====================

export const getAdminSiteDetails = async (siteId) => {
  const response = await Interceptor.get(`/admin/sites/${siteId}`);
  return response.data;
};

export const updateSiteStatus = async (siteId, status) => {
  const response = await Interceptor.put(`/admin/sites/${siteId}/status`, {
    status,
  });
  return response.data;
};

export const adminStartSite = async (siteId) => {
  const response = await Interceptor.post(`/admin/sites/${siteId}/start`);
  return response.data;
};

export const adminStopSite = async (siteId) => {
  const response = await Interceptor.post(`/admin/sites/${siteId}/stop`);
  return response.data;
};

export const adminRestartSite = async (siteId) => {
  const response = await Interceptor.post(`/admin/sites/${siteId}/restart`);
  return response.data;
};

export const adminSoftDeleteSite = async (siteId) => {
  const response = await Interceptor.delete(`/admin/sites/${siteId}`);
  return response.data;
};

export const adminPermanentDeleteSite = async (siteId) => {
  const response = await Interceptor.post(
    `/admin/sites/${siteId}/permanent-delete`,
  );
  return response.data;
};

export const adminRestoreSite = async (siteId) => {
  const response = await Interceptor.post(`/admin/sites/${siteId}/restore`);
  return response.data;
};

export const adminTransferSite = async (siteId, newOwnerId) => {
  const response = await Interceptor.put(`/admin/sites/${siteId}/transfer`, {
    newOwnerId,
  });
  return response.data;
};

// ==================== User Management ====================

export const adminPermanentDeleteUser = async (userId) => {
  const response = await Interceptor.delete(`/admin/users/${userId}/permanent`);
  return response.data;
};

// ==================== Application Management ====================

export const getAdminApplications = async (includeDeleted = false) => {
  const response = await Interceptor.get(
    `/admin/applications${includeDeleted ? "?includeDeleted=true" : ""}`,
  );
  return response.data;
};

export const adminPermanentDeleteApplication = async (appId) => {
  const response = await Interceptor.delete(
    `/admin/applications/${appId}/permanent`,
  );
  return response.data;
};

export const adminRestoreApplication = async (appId) => {
  const response = await Interceptor.post(
    `/admin/applications/${appId}/restore`,
  );
  return response.data;
};

// Application service actions (uses the standard applications API)
export const adminDeployApplication = async (appId) => {
  const response = await Interceptor.post(`/applications/${appId}/deploy`);
  return response.data;
};

export const adminRedeployApplication = async (appId) => {
  const response = await Interceptor.post(`/applications/${appId}/redeploy`);
  return response.data;
};

export const adminStartService = async (appId, serviceId) => {
  const response = await Interceptor.post(
    `/applications/${appId}/services/${serviceId}/start`,
  );
  return response.data;
};

export const adminStopService = async (appId, serviceId) => {
  const response = await Interceptor.post(
    `/applications/${appId}/services/${serviceId}/stop`,
  );
  return response.data;
};

export const adminRestartService = async (appId, serviceId) => {
  const response = await Interceptor.post(
    `/applications/${appId}/services/${serviceId}/restart`,
  );
  return response.data;
};

export const adminGetServiceLogs = async (appId, serviceId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.tail) queryParams.set("tail", params.tail);
  if (params.since) queryParams.set("since", params.since);
  const response = await Interceptor.get(
    `/applications/${appId}/services/${serviceId}/logs?${queryParams}`,
  );
  return response.data;
};
